import type {
  WorkflowBody,
  WorkflowStatusBody,
  WorkflowTransitionBody,
} from '@/shared/api/generated/model';

/*
 * Статусы записей (шаг 7.3) — чистые правила: какой статус у записи, какие
 * кнопки показать человеку, готовые наборы. Отдельно от компонентов — их
 * проверяют тесты.
 */

export type StageColor = NonNullable<WorkflowStatusBody['color']>;

export const STAGE_COLORS: { value: StageColor; label: string }[] = [
  { value: 'gray', label: 'Серый' },
  { value: 'blue', label: 'Синий' },
  { value: 'green', label: 'Зелёный' },
  { value: 'yellow', label: 'Жёлтый' },
  { value: 'orange', label: 'Оранжевый' },
  { value: 'red', label: 'Красный' },
  { value: 'purple', label: 'Фиолетовый' },
];

/** Статусы набора; null из API — пусто. */
export function statusesOf(w: WorkflowBody | undefined): WorkflowStatusBody[] {
  return w?.statuses ?? [];
}

/** Переходы набора; null из API — пусто. */
export function transitionsOf(w: WorkflowBody | undefined): WorkflowTransitionBody[] {
  return w?.transitions ?? [];
}

/** Статусы есть. */
export function hasStages(w: WorkflowBody | undefined): w is WorkflowBody {
  return statusesOf(w).length > 0;
}

/** Начальный: отмеченный, иначе первый. */
export function initialStage(w: WorkflowBody): string {
  const list = statusesOf(w);

  return (list.find((s) => s.initial === true) ?? list[0])?.key ?? '';
}

/** Статус записи; нет или удалён из набора — начальный (так считает и сервер). */
export function stageOf(
  w: WorkflowBody,
  stage: string | undefined,
): WorkflowStatusBody | undefined {
  const list = statusesOf(w);

  return list.find((s) => s.key === stage) ?? list.find((s) => s.key === initialStage(w));
}

/** Кнопки из статуса для роли; роли у перехода не заданы — всем, кто правит. */
export function actionsFor(
  w: WorkflowBody,
  from: string,
  role: string | undefined,
): WorkflowTransitionBody[] {
  // Владелец платформы на любом сайте — администратор: так его считает и
  // сервер. Без этого он не видел «Одобрить» у отзывов (31 октября 2026).
  const effective = role === 'super_admin' ? 'admin' : role;

  return transitionsOf(w).filter(
    (t) =>
      t.from === from &&
      (t.roles === undefined ||
        t.roles === null ||
        t.roles.length === 0 ||
        (effective !== undefined && t.roles.includes(effective))),
  );
}

/** Ключ статуса из названия: «В работе» → in_rabote. */
export function stageKey(name: string, taken: string[]): string {
  const map: Record<string, string> = {
    а: 'a',
    б: 'b',
    в: 'v',
    г: 'g',
    д: 'd',
    е: 'e',
    ё: 'e',
    ж: 'zh',
    з: 'z',
    и: 'i',
    й: 'y',
    к: 'k',
    л: 'l',
    м: 'm',
    н: 'n',
    о: 'o',
    п: 'p',
    р: 'r',
    с: 's',
    т: 't',
    у: 'u',
    ф: 'f',
    х: 'h',
    ц: 'c',
    ч: 'ch',
    ш: 'sh',
    щ: 'sch',
    ъ: '',
    ы: 'y',
    ь: '',
    э: 'e',
    ю: 'yu',
    я: 'ya',
  };

  const base =
    name
      .toLowerCase()
      .split('')
      .map((c) => map[c] ?? c)
      .join('')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^(\d)/, 's$1')
      .slice(0, 40) || 'status';

  let key = base;

  for (let n = 2; taken.includes(key); n++) {
    key = `${base}_${String(n)}`;
  }

  return key;
}

/** Готовые наборы — начать не с пустого. */
export const STAGE_PRESETS: { name: string; workflow: WorkflowBody }[] = [
  {
    name: 'Заявки: новая → в работе → закрыта',
    workflow: {
      statuses: [
        { key: 'new', name: 'Новая', color: 'blue', initial: true },
        { key: 'in_progress', name: 'В работе', color: 'yellow' },
        { key: 'done', name: 'Закрыта', color: 'green' },
        { key: 'spam', name: 'Спам', color: 'gray' },
      ],
      transitions: [
        { from: 'new', to: 'in_progress', label: 'Взять в работу', comment: 'none' },
        { from: 'new', to: 'spam', label: 'Спам', comment: 'none' },
        { from: 'in_progress', to: 'done', label: 'Закрыть', comment: 'optional' },
        { from: 'done', to: 'in_progress', label: 'Вернуть в работу', comment: 'none' },
      ],
    },
  },
  {
    name: 'Модерация: на проверке → одобрено / на доработку / отклонено',
    workflow: {
      statuses: [
        { key: 'review', name: 'На проверке', color: 'yellow', initial: true },
        { key: 'approved', name: 'Одобрено', color: 'green' },
        { key: 'rework', name: 'На доработку', color: 'orange' },
        { key: 'rejected', name: 'Отклонено', color: 'red' },
      ],
      transitions: [
        {
          from: 'review',
          to: 'approved',
          label: 'Одобрить',
          comment: 'none',
          roles: ['admin', 'editor'],
        },
        {
          from: 'review',
          to: 'rework',
          label: 'На доработку',
          comment: 'required',
          roles: ['admin', 'editor'],
        },
        {
          from: 'review',
          to: 'rejected',
          label: 'Отклонить',
          comment: 'required',
          roles: ['admin', 'editor'],
        },
        { from: 'rework', to: 'review', label: 'Отправить снова', comment: 'none', visitor: true },
      ],
    },
  },
  {
    name: 'Заказ: новый → оплачен → отправлен → получен',
    workflow: {
      statuses: [
        { key: 'new', name: 'Новый', color: 'blue', initial: true },
        { key: 'paid', name: 'Оплачен', color: 'purple' },
        { key: 'shipped', name: 'Отправлен', color: 'yellow' },
        { key: 'delivered', name: 'Получен', color: 'green' },
        { key: 'cancelled', name: 'Отменён', color: 'gray' },
      ],
      transitions: [
        { from: 'new', to: 'paid', label: 'Оплачен', comment: 'none' },
        {
          from: 'new',
          to: 'cancelled',
          label: 'Отменить заказ',
          comment: 'optional',
          visitor: true,
        },
        { from: 'paid', to: 'shipped', label: 'Отправить', comment: 'optional' },
        { from: 'shipped', to: 'delivered', label: 'Получен', comment: 'none' },
        { from: 'paid', to: 'cancelled', label: 'Отменить', comment: 'required' },
      ],
    },
  },
];
